// Calls -- 3CX call history for the office.
//
// Every outside call on the monitored extensions, written live by the
// 3CX listener and backfilled / topped up from 3CX's own call report
// every 15 minutes (which is also where recording links come from).
// Filters: date range, direction, result, person, search. "Not
// returned" = missed inbound with no answered call to/from that number
// since.
//
// Admins get an "Import from 3CX" panel for pulling older history and a
// raw-sample viewer for checking what 3CX sends.

import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext.jsx';
import CallsTable, { formatSeconds } from '../components/CallsTable.jsx';

const RANGES = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: '7', label: 'Last 7 days' },
  { id: '30', label: 'Last 30 days' },
  { id: '90', label: 'Last 90 days' },
  { id: '365', label: 'Last 12 months' },
  { id: 'all', label: 'All time' }
];

const PAGE = 100;

function rangeBounds(id) {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  if (id === 'today') return { from: start.toISOString() };
  if (id === 'yesterday') {
    const y = new Date(start); y.setDate(y.getDate() - 1);
    return { from: y.toISOString(), to: start.toISOString() };
  }
  if (id === 'all') return {};
  const d = new Date(start); d.setDate(d.getDate() - (Number(id) - 1));
  return { from: d.toISOString() };
}

function Tile({ label, value, tone }) {
  return (
    <div className={`calls-tile${tone ? ` calls-tile-${tone}` : ''}`}>
      <div className="calls-tile-value">{value}</div>
      <div className="calls-tile-label">{label}</div>
    </div>
  );
}

function ImportPanel({ onImported }) {
  const [days, setDays] = useState(365);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sample, setSample] = useState(null);
  const [checkExtNo, setCheckExtNo] = useState('105');

  const poll = useCallback(async () => {
    try {
      const r = await axios.get('/api/calls/import-status');
      setStatus(r.data);
      return r.data;
    } catch { return null; }
  }, []);

  useEffect(() => { poll(); }, [poll]);

  async function start() {
    setBusy(true);
    try {
      await axios.post('/api/calls/import', { days });
    } catch (err) {
      setStatus((s) => ({ ...(s || {}), lastError: err.response?.data?.error || err.message }));
      setBusy(false);
      return;
    }
    for (let i = 0; i < 120; i += 1) {
      await new Promise((r) => setTimeout(r, 2000));
      const s = await poll();
      if (s && !s.running) break;
    }
    setBusy(false);
    onImported();
  }

  async function checkExt() {
    setSample({ loading: true });
    try {
      const r = await axios.get('/api/calls/pbx-check', { params: { ext: checkExtNo, days: 7 } });
      setSample({ check: r.data });
    } catch (err) {
      setSample({ error: err.response?.data?.error || err.message });
    }
  }

  async function loadSample() {
    setSample({ loading: true });
    try {
      const r = await axios.get('/api/calls/pbx-sample', { params: { days: 7 } });
      setSample({ rows: r.data.rows });
    } catch (err) {
      setSample({ error: err.response?.data?.error || err.message });
    }
  }

  const res = status && status.lastResult;
  return (
    <details className="calls-import calls-import-top">
      <summary>3CX history import</summary>
      <div className="calls-import-body">
        <p className="calls-dim">
          New calls are added automatically. 3CX's call report is also checked every 15 minutes
          to fill in recordings and anything missed. Use this to pull older history.
        </p>
        <div className="calls-filters">
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} disabled={busy}>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last 12 months</option>
            <option value={730}>Last 2 years</option>
          </select>
          <button type="button" className="btn-primary btn-sm" onClick={start} disabled={busy}>
            {busy ? 'Importing…' : 'Import from 3CX'}
          </button>
          <button type="button" className="btn-secondary btn-sm" onClick={loadSample}>Show raw 3CX sample</button>
          <input
            type="text" value={checkExtNo} onChange={(e) => setCheckExtNo(e.target.value)}
            style={{ width: 70 }} aria-label="Extension to check" placeholder="Ext"
          />
          <button type="button" className="btn-secondary btn-sm" onClick={checkExt} disabled={!checkExtNo}>Check extension</button>
        </div>
        {status && status.lastError && <p className="calls-error">Last import failed: {status.lastError}</p>}
        {res && !status.lastError && (
          <p className="calls-dim">
            Last import {status.lastRunAt ? new Date(status.lastRunAt).toLocaleString('en-CA') : ''}: {res.calls} calls
            from {res.report_rows} report rows ({res.inserted} new, {res.updated} updated) over {res.days} day(s).
            {res.recording_parts_added ? ` Added ${res.recording_parts_added} extra recorded part(s) from transfers.` : ''}
          </p>
        )}
        {sample && sample.loading && <p className="calls-dim">Loading sample…</p>}
        {sample && sample.error && <p className="calls-error">{sample.error}</p>}
        {sample && sample.check && (
          <pre className="calls-sample">{JSON.stringify(sample.check, null, 2)}</pre>
        )}
        {sample && sample.rows && (
          <pre className="calls-sample">{sample.rows.length ? JSON.stringify(sample.rows, null, 2) : 'No rows in the last 7 days.'}</pre>
        )}
      </div>
    </details>
  );
}

function PhonebookPanel() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const poll = useCallback(async () => {
    try {
      const r = await axios.get('/api/calls/phonebook-status');
      setStatus(r.data);
      return r.data;
    } catch { return null; }
  }, []);

  useEffect(() => { poll(); }, [poll]);

  async function run(dryRun) {
    setBusy(true);
    try {
      await axios.post('/api/calls/phonebook-sync', { dryRun });
    } catch (err) {
      setStatus((s) => ({ ...(s || {}), lastError: err.response?.data?.error || err.message }));
      setBusy(false);
      return;
    }
    for (let i = 0; i < 300; i += 1) {
      await new Promise((r) => setTimeout(r, 2000));
      const s = await poll();
      if (s && !s.running) break;
    }
    setBusy(false);
  }

  const r = status && status.lastResult;
  return (
    <details className="calls-import calls-import-top">
      <summary>3CX phonebook sync</summary>
      <div className="calls-import-body">
        <p className="calls-dim">
          Every night at 2:15 a.m. Command copies customers with active sites into the 3CX company
          phonebook (site lines, main contact, sub-contacts) so the 3CX apps can search and dial them.
          Only entries Command created are ever changed or removed.
        </p>
        <div className="calls-filters">
          <button type="button" className="btn-secondary btn-sm" onClick={() => run(true)} disabled={busy}>Preview changes</button>
          <button type="button" className="btn-primary btn-sm" onClick={() => run(false)} disabled={busy}>
            {busy ? 'Working…' : 'Sync phonebook now'}
          </button>
        </div>
        {status && status.lastError && <p className="calls-error">Last sync failed: {status.lastError}</p>}
        {r && !status.lastError && (
          <p className="calls-dim">
            {r.dry_run ? 'Preview' : 'Last sync'} {status.lastRunAt ? new Date(status.lastRunAt).toLocaleString('en-CA') : ''}:
            {' '}{r.desired} entries from Command; {r.created} {r.dry_run ? 'to add' : 'added'}, {r.updated} {r.dry_run ? 'to update' : 'updated'},
            {' '}{r.deleted} {r.dry_run ? 'to remove' : 'removed'}, {r.unchanged} unchanged{r.failed ? `, ${r.failed} failed` : ''}.
            {r.delete_skipped ? ` ${r.delete_skipped}` : ''}
          </p>
        )}
        {status && status.errors && status.errors.length > 0 && (
          <pre className="calls-sample">{status.errors.join('\n')}</pre>
        )}
      </div>
    </details>
  );
}

export default function Calls() {
  const { user } = useAuth();
  const [range, setRange] = useState('7');
  const [direction, setDirection] = useState('');
  const [status, setStatus] = useState('');
  const [ext, setExt] = useState('');
  const [q, setQ] = useState('');
  const [qLive, setQLive] = useState('');
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  // Support emails from customers, shown in the same timeline.
  const [show, setShow] = useState(() => { try { return localStorage.getItem('calls_show') || 'both'; } catch { return 'both'; } });
  const [emails, setEmails] = useState([]);
  const [emailUsers, setEmailUsers] = useState([]);
  const [threadView, setThreadView] = useState('open');
  useEffect(() => { try { localStorage.setItem('calls_show', show); } catch { /* ignore */ } }, [show]);

  // Debounce the search box.
  useEffect(() => {
    const t = setTimeout(() => setQ(qLive.trim()), 300);
    return () => clearTimeout(t);
  }, [qLive]);

  useEffect(() => { setOffset(0); }, [range, direction, status, ext, q]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...rangeBounds(range), limit: PAGE, offset };
      if (direction) params.direction = direction;
      if (status) params.status = status;
      if (ext) params.ext = ext;
      if (q) params.q = q;
      const r = await axios.get('/api/calls', { params });
      setData(r.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [range, direction, status, ext, q, offset]);

  useEffect(() => { load(); }, [load]);

  // Emails only fit the "everything from customers" views: not for
  // outgoing/internal calls, call results, or one person's calls.
  const emailsApply = show !== 'calls' && !status && !ext && (direction === '' || direction === 'inbound' || direction === 'all');
  const loadEmails = useCallback(async () => {
    if (!emailsApply) { setEmails([]); return; }
    try {
      const params = { ...rangeBounds(range), thread: threadView };
      if (q) params.q = q;
      const r = await axios.get('/api/customer-emails/support-feed', { params });
      setEmails(r.data.rows || []);
      setEmailUsers(r.data.users || []);
    } catch { setEmails([]); }
  }, [emailsApply, range, q, threadView]);
  useEffect(() => { loadEmails(); }, [loadEmails]);
  useEffect(() => {
    const t = setInterval(loadEmails, 60 * 1000);
    return () => clearInterval(t);
  }, [loadEmails]);

  // With paging, each page shows the emails that fall between its calls.
  const pageEmails = (() => {
    if (!data || !emailsApply) return [];
    if (show === 'emails') return emails;
    const rows = data.rows || [];
    const lastPage = !data.totals || offset + PAGE >= data.totals.total;
    const oldest = rows.length ? new Date(rows[rows.length - 1].started_at).getTime() : 0;
    const newest = rows.length ? new Date(rows[0].started_at).getTime() : Infinity;
    return emails.filter((m) => {
      const t = new Date(m.activity_at || m.sent_at).getTime();
      return (lastPage || t >= oldest) && (offset === 0 || t <= newest);
    });
  })();

  // Refresh every minute so new calls appear without reloading.
  useEffect(() => {
    const t = setInterval(load, 60 * 1000);
    return () => clearInterval(t);
  }, [load]);

  const t = data && data.totals;

  return (
    <section className="page">
      <div className="page-header">
        <h2>Calls &amp; Emails</h2>
        <div className="subtitle">Office phone history from 3CX plus support emails from customers, newest first. Missed calls nobody has returned are flagged.</div>
      </div>

      {user?.role === 'admin' && <ImportPanel onImported={load} />}
      {user?.role === 'admin' && <PhonebookPanel />}

      {t && (
        <div className="calls-tiles">
          <Tile label="Calls" value={t.total} />
          <Tile label="Answered" value={t.answered} tone="good" />
          <Tile label="Missed" value={t.missed} tone={t.missed ? 'warn' : ''} />
          <Tile label="Not returned" value={t.unreturned} tone={t.unreturned ? 'bad' : ''} />
          <Tile label="Talk time" value={formatSeconds(t.talk_seconds) || '0s'} />
          <Tile label="Avg time to answer" value={t.avg_answer_seconds != null ? formatSeconds(t.avg_answer_seconds) : 'n/a'} />
          {emailsApply && <Tile label={threadView === 'open' ? 'Open support emails' : 'Support emails'} value={emails.length} tone={threadView === 'open' && emails.some((m) => m.waiting_on === 'us') ? 'warn' : ''} />}
        </div>
      )}

      <div className="calls-filters">
        <select value={show} onChange={(e) => setShow(e.target.value)}>
          <option value="both">Calls &amp; support emails</option>
          <option value="calls">Calls only</option>
          <option value="emails">Support emails only</option>
        </select>
        {emailsApply && (
          <select value={threadView} onChange={(e) => setThreadView(e.target.value)} title="Which support threads to show">
            <option value="open">Open emails</option>
            <option value="unassigned">Unassigned</option>
            <option value="mine">Assigned to me</option>
            <option value="done">Done</option>
            <option value="all">All emails</option>
          </select>
        )}
        <select value={range} onChange={(e) => setRange(e.target.value)}>
          {RANGES.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
        </select>
        <select value={direction} onChange={(e) => setDirection(e.target.value)}>
          <option value="">In and out</option>
          <option value="inbound">Incoming</option>
          <option value="outbound">Outgoing</option>
          <option value="internal">Internal (office)</option>
          <option value="all">Everything</option>
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All results</option>
          <option value="answered">Answered</option>
          <option value="missed">Missed</option>
          <option value="unreturned">Not returned</option>
          <option value="recorded">Recorded</option>
        </select>
        <select value={ext} onChange={(e) => setExt(e.target.value)}>
          <option value="">Everyone</option>
          {(data?.extensions || []).map((x) => <option key={x.ext} value={x.ext}>{x.name} ({x.ext})</option>)}
        </select>
        <input
          type="search"
          className="calls-search"
          placeholder="Search customer, site or number"
          value={qLive}
          onChange={(e) => setQLive(e.target.value)}
        />
        {loading && <span className="calls-dim">Loading…</span>}
      </div>

      {error && <p className="calls-error">Could not load calls: {error}</p>}

      {data && (
        <CallsTable
          rows={show === 'emails' ? [] : data.rows}
          emails={pageEmails}
          emailUsers={emailUsers}
          onEmailChanged={loadEmails}
          emptyText={show === 'emails' ? 'No support emails in this period.' : 'No calls match these filters.'}
        />
      )}

      {data && t && t.total > PAGE && show !== 'emails' && (
        <div className="calls-pager">
          <button type="button" className="btn-secondary btn-sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>Newer</button>
          <span className="calls-dim">{offset + 1}–{Math.min(offset + PAGE, t.total)} of {t.total}</span>
          <button type="button" className="btn-secondary btn-sm" disabled={offset + PAGE >= t.total} onClick={() => setOffset(offset + PAGE)}>Older</button>
        </div>
      )}

    </section>
  );
}
